import React, { useEffect, useMemo, useState } from 'react';
import { api, getToken } from '../api.js';
import TagPill from '../components/TagPill.jsx';
import Modal from '../components/Modal.jsx';
import { fmtIST } from '../lib/datetime.js';

/* Top-level Campaigns page — keeps three sub-views in one component to
 * avoid wiring a real router for now.
 *   list     → all campaigns
 *   create   → new-campaign form
 *   detail   → one campaign's settings + audience preview + send controls
 */
export default function Campaigns({ tags, initialOpenId, onOpened }) {
  const [view, setView]   = useState('list'); // 'list' | 'create' | 'detail'
  const [campaigns, setCampaigns] = useState([]);
  const [activeId, setActiveId]   = useState(null);

  async function load() {
    try { const r = await api.get('/api/campaigns'); setCampaigns(r.rows || []); } catch {}
  }
  useEffect(() => { load(); }, []);

  // Open a specific campaign when navigated from Reports.
  useEffect(() => {
    if (initialOpenId) {
      setActiveId(initialOpenId);
      setView('detail');
      onOpened && onOpened();
    }
  }, [initialOpenId, onOpened]);

  function gotoDetail(id) { setActiveId(id); setView('detail'); }
  function gotoList()     { setActiveId(null); setView('list'); load(); }

  return (
    <div className="px-8 py-6 max-w-[1400px] mx-auto">
      {view === 'list' && (
        <CampaignsList
          campaigns={campaigns}
          tags={tags}
          onCreate={() => setView('create')}
          onOpen={gotoDetail}
        />
      )}
      {view === 'create' && (
        <CampaignCreate
          tags={tags}
          onCancel={gotoList}
          onCreated={(id) => gotoDetail(id)}
        />
      )}
      {view === 'detail' && activeId && (
        <CampaignDetail
          id={activeId}
          tags={tags}
          onBack={gotoList}
        />
      )}
    </div>
  );
}

/* ============================================================================
 * LIST
 * ============================================================================ */
function CampaignsList({ campaigns, tags, onCreate, onOpen }) {
  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Campaigns</h1>
          <p className="text-sm text-slate-500 mt-0.5">Tag-based outreach over WhatsApp + Email with manual retries.</p>
        </div>
        <button className="btn" onClick={onCreate}>+ New campaign</button>
      </div>

      {!campaigns.length ? (
        <div className="card p-12 text-center">
          <div className="text-slate-400 text-sm">No campaigns yet.</div>
          <button className="btn mt-4" onClick={onCreate}>Create your first campaign</button>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map(c => (
            <button
              key={c.id}
              onClick={() => onOpen(c.id)}
              className="card p-5 w-full text-left hover:border-slate-300 transition flex items-center gap-5"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="text-base font-semibold text-slate-900 truncate">{c.name}</h3>
                  <StatusBadge status={c.status} />
                  {c.send_wa && <span className="text-[11px] uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">WA</span>}
                  {c.send_email && <span className="text-[11px] uppercase tracking-wider text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">Email</span>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {tagSlugsToPills(c.filter_tag_slugs, tags).map(t => <TagPill key={t.id} tag={t} />)}
                  {c.exclude_tag_slugs && tagSlugsToPills(c.exclude_tag_slugs, tags).map(t => (
                    <span key={'ex-'+t.id} className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 text-rose-700 px-2 py-0.5 text-xs font-medium" title="Excluded">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />– {t.name}
                    </span>
                  ))}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs text-slate-500 uppercase tracking-wider">Recipients</div>
                <div className="text-lg font-semibold text-slate-900">{Number(c.recipients).toLocaleString()}</div>
                <div className="text-xs text-slate-500 mt-0.5">{Number(c.sent_count).toLocaleString()} sent</div>
              </div>
              <svg className="w-5 h-5 text-slate-300 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6"/></svg>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft:     'bg-slate-100 text-slate-600',
    queued:    'bg-amber-50 text-amber-700',
    running:   'bg-emerald-50 text-emerald-700',
    paused:    'bg-amber-50 text-amber-700',
    done:      'bg-slate-100 text-slate-500',
    cancelled: 'bg-slate-100 text-slate-500',
  };
  return <span className={`text-[11px] uppercase tracking-wider px-1.5 py-0.5 rounded ${map[status] || 'bg-slate-100 text-slate-600'}`}>{status}</span>;
}

function tagSlugsToPills(slugStr, tags) {
  if (!slugStr) return [];
  const wanted = new Set(slugStr.split(',').map(s => s.trim()).filter(Boolean));
  return tags.filter(t => wanted.has(t.slug));
}

/* ============================================================================
 * CREATE
 * ============================================================================ */
function CampaignCreate({ tags, onCancel, onCreated }) {
  const [name, setName] = useState('');
  const [includeSlugs, setIncludeSlugs] = useState(new Set());
  const [excludeSlugs, setExcludeSlugs] = useState(new Set());
  const [requireValidPhone, setRequireValidPhone] = useState(true);
  const [sendWa, setSendWa] = useState(true);
  const [sendEmail, setSendEmail] = useState(true);
  const [waTemplate, setWaTemplate] = useState(localStorage.getItem('iaapi_wa_template') || 'expo2026_membership_upsell_1');
  const [waLanguage, setWaLanguage] = useState('en');
  const [waBindings, setWaBindings] = useState({ body: [], button_url: null });
  const [emailTemplateId, setEmailTemplateId] = useState('custom');
  const [emailSubject, setEmailSubject] = useState('Your IAAPI Membership Invitation — Exclusive Discount for Expo 2026 Attendees');
  const [emailHtml, setEmailHtml] = useState('');
  const [emailHtmlLoaded, setEmailHtmlLoaded] = useState(false);
  const [waOptions, setWaOptions] = useState([]);
  const [emailOptions, setEmailOptions] = useState([]);
  const [linkKind, setLinkKind] = useState('form');
  const [redirectUrl, setRedirectUrl] = useState('');
  const [formId, setFormId] = useState(null);
  const [formOptions, setFormOptions] = useState([]);
  const [batchLimit, setBatchLimit] = useState(25);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Pre-fill the textarea with the branded IAAPI default + load template lists.
  useEffect(() => {
    let cancelled = false;
    api.get('/api/templates/email-default').then(html => {
      if (!cancelled && !emailHtmlLoaded) {
        setEmailHtml(typeof html === 'string' ? html : String(html || ''));
        setEmailHtmlLoaded(true);
      }
    }).catch(() => { if (!cancelled) setEmailHtmlLoaded(true); });
    api.get('/api/wa-templates').then(r => {
      if (!cancelled) {
        const approved = (r.rows || []).filter(t => t.status === 'APPROVED');
        setWaOptions(approved);
        // Seed bindings if a template name is already pre-filled (from
        // localStorage) — picking from the dropdown would have done this,
        // but a pre-filled value never fires onChange.
        if (waTemplate && (!waBindings.body?.length && !waBindings.button_url)) {
          const tpl = approved.find(t => t.name === waTemplate);
          if (tpl) {
            const v = tpl.variables || {};
            const body = Array.from({ length: v.body_count || 0 }, (_, i) =>
              i === 0 ? { type: 'contact', field: 'first_name' } : { type: 'static', value: '' }
            );
            const button_url = (v.button_url_count || 0) > 0 ? { type: 'token_path_channel' } : null;
            setWaBindings({ body, button_url });
          }
        }
      }
    }).catch(() => {});
    api.get('/api/email-templates').then(r => {
      if (!cancelled) setEmailOptions(r.rows || []);
    }).catch(() => {});
    api.get('/api/forms').then(r => {
      if (!cancelled) {
        setFormOptions(r.rows || []);
        const def = (r.rows || []).find(f => f.slug === 'iaapi-membership-signup');
        if (def && !formId) setFormId(def.id);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the user picks a WA template, seed bindings to sensible defaults
  // matching that template's variable layout. Operator can override each.
  function pickWaTemplate(name) {
    setWaTemplate(name);
    localStorage.setItem('iaapi_wa_template', name);
    const tpl = waOptions.find(t => t.name === name);
    if (!tpl) { setWaBindings({ body: [], button_url: null }); return; }
    const v = tpl.variables || {};
    const body = Array.from({ length: v.body_count || 0 }, (_, i) =>
      i === 0 ? { type: 'contact', field: 'first_name' } : { type: 'static', value: '' }
    );
    const button_url = (v.button_url_count || 0) > 0 ? { type: 'token_path_channel' } : null;
    setWaBindings({ body, button_url });
  }

  async function pickEmailTemplate(idStr) {
    setEmailTemplateId(idStr);
    if (idStr === 'custom') return;
    if (idStr === 'default') {
      try { const html = await api.get('/api/templates/email-default'); setEmailHtml(String(html || '')); } catch {}
      return;
    }
    try {
      const r = await api.get(`/api/email-templates/${idStr}`);
      setEmailSubject(r.template.subject);
      setEmailHtml(r.template.html);
    } catch (e) { alert(e.message); }
  }

  function toggleSet(setter, current, slug) {
    const next = new Set(current);
    if (next.has(slug)) next.delete(slug); else next.add(slug);
    setter(next);
  }

  async function submit() {
    setErr('');
    if (!name.trim()) return setErr('Name is required');
    if (!sendWa && !sendEmail) return setErr('Pick at least one channel');
    if (sendWa && !waTemplate.trim()) return setErr('WA template name is required');
    if (sendEmail && !emailSubject.trim()) return setErr('Email subject is required');
    if (linkKind === 'redirect' && !redirectUrl.trim()) return setErr('Redirect URL is required');
    setBusy(true);
    try {
      const r = await api.post('/api/campaigns', {
        name: name.trim(),
        filter_tag_slugs:  [...includeSlugs].join(','),
        exclude_tag_slugs: [...excludeSlugs].join(','),
        require_valid_phone: requireValidPhone,
        send_wa: sendWa,
        send_email: sendEmail,
        wa_template_name: waTemplate.trim(),
        wa_template_language: waLanguage,
        wa_template_bindings: sendWa ? waBindings : null,
        email_subject: emailSubject.trim(),
        email_html: emailHtml,
        link_kind: linkKind,
        redirect_url: redirectUrl.trim(),
        form_id: linkKind === 'form' ? formId : null,
        batch_limit: Number(batchLimit) || 25,
      });
      onCreated(r.id);
    } catch (e) { setErr(e.body?.error || e.message); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <button className="text-sm text-slate-500 hover:text-slate-700 mb-1.5" onClick={onCancel}>← All campaigns</button>
          <h1 className="text-xl font-semibold text-slate-900">New campaign</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          {/* Step 1 — Name */}
          <Section title="1. Name" hint="Internal label only — recipients don't see this.">
            <input className="input w-full" placeholder="IAAPI Expo 2026 Membership Drive" value={name} onChange={e => setName(e.target.value)} />
          </Section>

          {/* Step 2 — Audience */}
          <Section title="2. Audience" hint="Pick tags to include. Use exclude to subtract groups (e.g. org committee).">
            <div className="space-y-3">
              <div>
                <div className="label-l">Include tags</div>
                <TagSelect tags={tags} selected={includeSlugs} onToggle={(slug) => toggleSet(setIncludeSlugs, includeSlugs, slug)} />
              </div>
              <div>
                <div className="label-l">Exclude tags</div>
                <TagSelect tags={tags} selected={excludeSlugs} onToggle={(slug) => toggleSet(setExcludeSlugs, excludeSlugs, slug)} variant="rose" />
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-slate-600 mt-2">
                <input type="checkbox" checked={requireValidPhone} onChange={e => setRequireValidPhone(e.target.checked)} />
                Skip contacts with invalid phone numbers (recommended)
              </label>
            </div>
          </Section>

          {/* Step 3 — Channels */}
          <Section title="3. Channels">
            <div className="flex gap-3">
              <label className="flex-1 cursor-pointer">
                <div className={`card p-4 ${sendWa ? 'border-brand-500 ring-1 ring-brand-500' : 'border-slate-200'}`}>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={sendWa} onChange={e => setSendWa(e.target.checked)} />
                    <span className="font-semibold text-slate-900">WhatsApp</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Pre-approved Meta template message with image header + URL button.</div>
                </div>
              </label>
              <label className="flex-1 cursor-pointer">
                <div className={`card p-4 ${sendEmail ? 'border-brand-500 ring-1 ring-brand-500' : 'border-slate-200'}`}>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} />
                    <span className="font-semibold text-slate-900">Email</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">SendGrid via expo2026@iaapi.in. HTML body with branded CTA button.</div>
                </div>
              </label>
            </div>
          </Section>

          {/* Step 4 — WA content */}
          {sendWa && (
            <Section title="4. WhatsApp content" hint="Pick from the approved templates fetched from Meta. Refresh the cache via Content → WhatsApp.">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <div className="label-l">Template</div>
                  <select className="input w-full" value={waTemplate} onChange={e => pickWaTemplate(e.target.value)}>
                    <option value="">— pick a template —</option>
                    {waOptions.map(t => <option key={t.id} value={t.name}>{t.name} ({t.language})</option>)}
                  </select>
                </div>
                <div>
                  <div className="label-l">Language</div>
                  <input className="input w-full" value={waLanguage} onChange={e => setWaLanguage(e.target.value)} placeholder="en" />
                </div>
              </div>
              {waTemplate && waOptions.find(t => t.name === waTemplate) && (
                <>
                  <WaTemplateSummary template={waOptions.find(t => t.name === waTemplate)} />
                  <WaBindingsEditor
                    template={waOptions.find(t => t.name === waTemplate)}
                    bindings={waBindings}
                    onChange={setWaBindings}
                  />
                </>
              )}
            </Section>
          )}

          {/* Step 5 — Email content */}
          {sendEmail && (
            <Section title={sendWa ? '5. Email content' : '4. Email content'}>
              <div className="space-y-3">
                <div>
                  <div className="label-l">Template</div>
                  <select className="input w-full" value={emailTemplateId} onChange={e => pickEmailTemplate(e.target.value)}>
                    <option value="custom">Custom (edit below)</option>
                    <option value="default">IAAPI default (branded)</option>
                    {emailOptions.length > 0 && <optgroup label="Saved templates">
                      {emailOptions.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </optgroup>}
                  </select>
                </div>
                <div>
                  <div className="label-l">Subject</div>
                  <input className="input w-full" value={emailSubject} onChange={e => setEmailSubject(e.target.value)} />
                </div>
                <div>
                  <div className="label-l flex items-center justify-between">
                    <span>HTML body</span>
                    <button type="button" className="text-[11px] text-brand-600 hover:underline"
                            onClick={() => api.get('/api/templates/email-default').then(html => setEmailHtml(String(html || '')))}>
                      Reset to IAAPI default
                    </button>
                  </div>
                  <textarea className="input w-full h-48 font-mono text-xs" value={emailHtml} onChange={e => setEmailHtml(e.target.value)} placeholder="Loading default…" />
                  <div className="text-[11px] text-slate-500 mt-1">Use <code>{'{{first_name}}'}</code> and <code>{'{{link_url}}'}</code> as placeholders. Save reusable bodies via <strong>Content → Email templates</strong>.</div>
                </div>
              </div>
            </Section>
          )}

          {/* Step 6 — Link */}
          <Section title={`${sendEmail && sendWa ? '6' : '5'}. Where the link goes`}>
            <div className="space-y-2">
              <LinkOption checked={linkKind === 'form'}
                          onClick={() => setLinkKind('form')}
                          title="Form (data collection)"
                          desc="Recipient fills a form. Submissions land in that form's data bank. Pick which form below." />
              <LinkOption checked={linkKind === 'redirect'}
                          onClick={() => setLinkKind('redirect')}
                          title="Redirect to a URL (outreach only)"
                          desc="Click is tracked, then we 302 to the URL. No data collection." />
              <LinkOption checked={linkKind === 'none'}
                          onClick={() => setLinkKind('none')}
                          title="No link (delivery only)"
                          desc="Useful for plain announcement messages. We still record delivery + read." />
              {linkKind === 'form' && (
                <div>
                  <div className="label-l mt-1">Form</div>
                  <select className="input w-full" value={formId || ''} onChange={e => setFormId(Number(e.target.value) || null)}>
                    <option value="">— pick a form —</option>
                    {formOptions.map(f => <option key={f.id} value={f.id}>{f.name} ({f.field_count} fields)</option>)}
                  </select>
                  <div className="text-[11px] text-slate-500 mt-1">Submissions land in this form's data bank under <strong>Forms → {formOptions.find(f => f.id === formId)?.name || '…'}</strong>.</div>
                </div>
              )}
              {linkKind === 'redirect' && (
                <input className="input w-full" placeholder="https://iaapi.org/membership"
                       value={redirectUrl} onChange={e => setRedirectUrl(e.target.value)} />
              )}
            </div>
          </Section>

          {/* Step 7 — Safety */}
          <Section title="Safety">
            <div className="flex items-center gap-3">
              <div className="label-l mb-0">First-run cap:</div>
              <input className="input w-24" type="number" min="0" value={batchLimit} onChange={e => setBatchLimit(e.target.value)} />
              <div className="text-xs text-slate-500">contacts (set 0 for no cap; you can always send more later).</div>
            </div>
          </Section>

          {err && <div className="text-sm text-rose-600">{err}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button className="btn-ghost" onClick={onCancel}>Cancel</button>
            <button className="btn" onClick={submit} disabled={busy}>{busy ? 'Creating…' : 'Create draft'}</button>
          </div>
        </div>

        <aside className="lg:sticky lg:top-6 self-start">
          <div className="card p-5">
            <h4 className="text-sm font-semibold text-slate-900 mb-2">How it works</h4>
            <ol className="text-xs text-slate-600 space-y-2 list-decimal list-inside">
              <li>You create the draft.</li>
              <li>Audience preview shows the live count.</li>
              <li>"Send first 25" queues a small pilot. Worker dispatches them and you watch delivery.</li>
              <li>Looks good? "Send all" queues the rest.</li>
              <li>Failed sends with retryable Meta codes go into a "Failed — retryable" bucket. You manually retry from the campaign dashboard. Up to 7 attempts per contact.</li>
            </ol>
          </div>
        </aside>
      </div>
    </>
  );
}

function Section({ title, hint, children }) {
  return (
    <div className="card p-5">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function TagSelect({ tags, selected, onToggle, variant = 'brand' }) {
  if (!tags.length) return <div className="text-xs text-slate-400">No tags yet. Create some on the Tags page.</div>;
  return (
    <div className="flex flex-wrap gap-2">
      {tags.map(t => {
        const on = selected.has(t.slug);
        const base = on
          ? variant === 'rose'
            ? 'bg-rose-50 border-rose-300 text-rose-700'
            : 'bg-brand-50 border-brand-300 text-brand-700'
          : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300';
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onToggle(t.slug)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition ${base}`}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
            {t.name}
            <span className="text-[10px] text-slate-400">{Number(t.contact_count).toLocaleString()}</span>
          </button>
        );
      })}
    </div>
  );
}

/* Variable-binding UI for the selected WA template. Lets the operator
 * map each {{N}} body var (and the URL button param) to a contact field,
 * a literal value, or the per-recipient token path. */
const WA_CONTACT_FIELDS = [
  { key: 'first_name',  label: 'First name (auto-derived)' },
  { key: 'name',        label: 'Full name' },
  { key: 'phone',       label: 'Phone (with country code)' },
  { key: 'email',       label: 'Email' },
  { key: 'org_name',    label: 'Organisation' },
  { key: 'designation', label: 'Designation / role' },
  { key: 'city',        label: 'City' },
  { key: 'state',       label: 'State' },
  { key: 'country',     label: 'Country' },
];

function WaBindingsEditor({ template, bindings, onChange }) {
  const v = template.variables || {};
  const bodyCount = v.body_count || 0;
  const buttonUrlCount = v.button_url_count || 0;

  function updateBody(idx, patch) {
    const body = [...(bindings.body || [])];
    body[idx] = { ...(body[idx] || {}), ...patch };
    onChange({ ...bindings, body });
  }
  function updateButton(patch) {
    onChange({ ...bindings, button_url: { ...(bindings.button_url || {}), ...patch } });
  }

  if (bodyCount === 0 && buttonUrlCount === 0) return null;

  return (
    <div className="mt-3 rounded-lg border border-slate-200 p-4 bg-white">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Variable bindings</div>
      <div className="text-xs text-slate-500 mb-3">Each <code>{'{{n}}'}</code> in the template gets resolved per recipient at send time.</div>
      <div className="space-y-2.5">
        {Array.from({ length: bodyCount }, (_, i) => (
          <BindingRow
            key={`body-${i}`}
            label={`Body {{${i + 1}}}`}
            spec={bindings.body?.[i] || { type: 'contact', field: 'first_name' }}
            onChange={patch => updateBody(i, patch)}
          />
        ))}
        {buttonUrlCount > 0 && (
          <BindingRow
            label="Button URL {{1}}"
            spec={bindings.button_url || { type: 'token_path_channel' }}
            onChange={updateButton}
            allowTokenPath
          />
        )}
      </div>
      {v.button_url_pattern && (
        <div className="mt-2 text-[11px] text-slate-500">
          Template URL: <code className="font-mono">{v.button_url_pattern}</code>
        </div>
      )}
    </div>
  );
}

function BindingRow({ label, spec, onChange, allowTokenPath = false }) {
  const types = [
    { key: 'contact', label: 'Contact field' },
    { key: 'static',  label: 'Static text' },
  ];
  if (allowTokenPath) types.unshift({ key: 'token_path_channel', label: 'Token path (e.g. r/wa/<token>)' });
  return (
    <div className="grid grid-cols-12 gap-2 items-center">
      <div className="col-span-3 text-xs font-mono text-slate-700">{label}</div>
      <div className="col-span-3">
        <select className="input w-full h-9 text-xs" value={spec.type} onChange={e => {
          const t = e.target.value;
          if (t === 'contact')             onChange({ type: 'contact', field: 'first_name' });
          else if (t === 'static')         onChange({ type: 'static', value: '' });
          else if (t === 'token_path_channel') onChange({ type: 'token_path_channel' });
        }}>
          {types.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </div>
      <div className="col-span-6">
        {spec.type === 'contact' && (
          <select className="input w-full h-9 text-xs" value={spec.field || 'first_name'} onChange={e => onChange({ ...spec, field: e.target.value })}>
            {WA_CONTACT_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        )}
        {spec.type === 'static' && (
          <input className="input w-full h-9 text-xs" value={spec.value || ''} onChange={e => onChange({ ...spec, value: e.target.value })} placeholder="Literal text" />
        )}
        {spec.type === 'token_path_channel' && (
          <div className="text-[11px] text-slate-500 italic h-9 flex items-center">Auto: <code className="ml-1.5 font-mono">r/wa/&lt;token&gt;</code></div>
        )}
      </div>
    </div>
  );
}

function WaTemplateSummary({ template }) {
  const v = template.variables || {};
  return (
    <div className="mt-3 rounded-lg border border-slate-200 p-3 bg-slate-50/60 text-xs space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider text-slate-500">Status</span>
        <span className="font-semibold text-emerald-700">{template.status}</span>
        <span className="text-slate-400">·</span>
        <span className="text-slate-600">{template.category || '—'}</span>
      </div>
      <div className="text-slate-600">
        Header: <strong>{v.header_type || 'TEXT'}</strong>{' · '}
        Body vars: <strong>{v.body_count || 0}</strong>{' · '}
        URL button: {v.button_url_pattern
          ? <code className="font-mono text-slate-700">{v.button_url_pattern}</code>
          : <span className="text-slate-500">none</span>}
      </div>
      <div className="text-slate-500">
        Auto-bindings: body <code>{'{{1}}'}</code> → first name; URL <code>{'{{1}}'}</code> → <code>r/&lt;token&gt;</code> (when present).
      </div>
    </div>
  );
}

function LinkOption({ checked, onClick, title, desc }) {
  return (
    <button type="button" onClick={onClick}
      className={`w-full text-left flex gap-3 p-3 rounded-lg border transition ${checked ? 'border-brand-500 ring-1 ring-brand-500 bg-brand-50/40' : 'border-slate-200 hover:border-slate-300'}`}>
      <div className={`mt-0.5 w-4 h-4 rounded-full border-4 ${checked ? 'border-brand-600' : 'border-slate-300'}`} />
      <div>
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
      </div>
    </button>
  );
}

/* ============================================================================
 * DETAIL
 * ============================================================================ */
function CampaignDetail({ id, tags, onBack }) {
  const [data, setData]       = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy]       = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(null); // 'first_n' | 'all' | null
  const [msg, setMsg] = useState('');

  async function load() {
    try { setData(await api.get(`/api/campaigns/${id}`)); } catch (e) { setMsg('Load failed: ' + e.message); }
  }
  async function loadPreview() {
    try { setPreview(await api.post(`/api/campaigns/${id}/preview`)); } catch {}
  }
  useEffect(() => { load(); loadPreview(); }, [id]);

  // batchSize is the operator-chosen amount for the next bucket. Defaults
  // to the campaign's batch_limit (25), but can be set to anything via
  // the input or preset chips on the Send card.
  const [batchSize, setBatchSize] = useState(0);   // 0 = "use campaign default"
  async function queue(mode, customLimit = null) {
    setBusy(true); setMsg('');
    try {
      const limit = customLimit || batchSize || (data.campaign.batch_limit || 25);
      const r = await api.post(`/api/campaigns/${id}/queue`, mode === 'all' ? { mode: 'all' } : { mode: 'first_n', limit });
      const tail = r.remaining_after > 0 ? ` · ${r.remaining_after.toLocaleString()} contacts still remaining for the next bucket` : '';
      setMsg(`Queued ${r.queued} send${r.queued === 1 ? '' : 's'}, skipped ${r.skipped}${tail}.`);
      await Promise.all([load(), loadPreview()]);
    } catch (e) { setMsg('Queue failed: ' + (e.body?.error || e.message)); }
    finally { setBusy(false); setConfirmOpen(null); }
  }

  async function retryFailed() {
    setBusy(true); setMsg('');
    try {
      const r = await api.post(`/api/campaigns/${id}/retry-failed`, {});
      setMsg(`Re-queued ${r.requeued} failed-retryable send${r.requeued === 1 ? '' : 's'}.`);
      await load();
    } catch (e) { setMsg('Retry failed: ' + (e.body?.error || e.message)); }
    finally { setBusy(false); }
  }

  async function changeStatus(target, confirmText) {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true); setMsg('');
    try {
      await api.post(`/api/campaigns/${id}/status`, { status: target });
      await load();
    } catch (e) { setMsg('Status change failed: ' + (e.body?.error || e.message)); }
    finally { setBusy(false); }
  }

  async function refreshAll() {
    setBusy(true);
    try { await Promise.all([load(), loadPreview()]); }
    finally { setBusy(false); }
  }

  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;
  const c = data.campaign;
  const counts = data.counts || {};

  return (
    <>
      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="flex-1 min-w-0">
          <button className="text-sm text-slate-500 hover:text-slate-700 mb-1.5" onClick={onBack}>← All campaigns</button>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-slate-900 truncate">{c.name}</h1>
            <StatusBadge status={c.status} />
          </div>
          <div className="text-sm text-slate-500 mt-1">
            {c.send_wa  && <span className="mr-2">WA: <code>{c.wa_template_name}</code></span>}
            {c.send_email && <span>Email: {c.email_subject}</span>}
          </div>
        </div>
        <button className="btn-ghost btn-sm shrink-0" onClick={refreshAll} disabled={busy} title="Reload counts and preview">
          {busy ? '…' : '↻ Refresh'}
        </button>
      </div>

      {/* counts */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Stat label="Recipients" value={Number(counts.total || 0)} />
        <Stat label="Queued"     value={Number(counts.queued || 0)} tone="amber" />
        <Stat label="Sending"    value={Number(counts.sending || 0)} tone="brand" />
        <Stat label="Sent"       value={Number(counts.sent || 0)} tone="emerald" />
        <Stat label="Failed (retryable)" value={Number(counts.failed_retryable || 0)} tone="rose" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          {/* test send panel — sits above audience so it's the first thing reached for */}
          <TestSendPanel campaignId={id} />

          {/* audience preview */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Audience preview</h3>
                <p className="text-xs text-slate-500 mt-0.5">{preview ? `${preview.total.toLocaleString()} contacts match` : 'Computing…'}</p>
              </div>
              <button className="btn-ghost btn-sm" onClick={loadPreview}>Refresh</button>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {tagSlugsToPills(c.filter_tag_slugs, tags).map(t => <TagPill key={t.id} tag={t} />)}
              {tagSlugsToPills(c.exclude_tag_slugs, tags).map(t => (
                <span key={'ex-'+t.id} className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 text-rose-700 px-2 py-0.5 text-xs font-medium">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />– {t.name}
                </span>
              ))}
              {!c.filter_tag_slugs && !c.exclude_tag_slugs && <span className="text-xs text-slate-400">No tag filter — would match everyone</span>}
            </div>
            {preview?.sample?.length > 0 && (
              <div className="rounded-lg border border-slate-100 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr><th className="px-3 py-2 text-left">Sample contacts</th><th className="px-3 py-2 text-left">Phone</th><th className="px-3 py-2 text-left">Email</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {preview.sample.map(s => (
                      <tr key={s.id}><td className="px-3 py-2 text-slate-700">{s.name}</td><td className="px-3 py-2 text-slate-500">+{s.phone}</td><td className="px-3 py-2 text-slate-500">{s.email}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {msg && <div className="text-sm text-slate-700 bg-slate-50 border border-slate-200 px-3 py-2 rounded-lg">{msg}</div>}

          <DashboardSection campaignId={id} />
        </div>

        <aside className="lg:sticky lg:top-6 self-start space-y-3">
          {(c.status === 'queued' || c.status === 'running') && Number(counts.queued || 0) === 0 && Number(counts.sending || 0) === 0 && Number(counts.failed_retryable || 0) === 0 && Number(counts.total || 0) > 0 && (
            <div className="card p-5 border-emerald-200 bg-emerald-50/30">
              <h4 className="text-sm font-semibold text-emerald-800 mb-1">All sends processed</h4>
              <p className="text-xs text-emerald-700 mb-3">Nothing pending and no retryable failures left. Want to mark this campaign complete?</p>
              <button className="btn w-full" disabled={busy} onClick={() => changeStatus('done', 'Mark this campaign complete? You can\'t resume after this — only "cancelled" → "done" is one-way.')}>Mark complete</button>
            </div>
          )}

          {(['draft','queued','running','paused'].includes(c.status)) && (() => {
            const audienceTotal = preview?.total ?? 0;
            const sentSoFar = Number(counts.total || 0);
            const remaining = Math.max(0, audienceTotal - sentSoFar);
            const isFirst = sentSoFar === 0;
            const effectiveBatch = Math.min(batchSize || (c.batch_limit || 25), remaining || 1);
            const verb = isFirst ? 'Send first' : 'Send next';
            const presets = [25, 100, 500, 1000].filter(n => n <= remaining || remaining === 0);
            return (
              <div className="card p-5">
                <h4 className="text-sm font-semibold text-slate-900 mb-1">Send</h4>
                <p className="text-xs text-slate-500 mb-3">{remaining.toLocaleString()} of {audienceTotal.toLocaleString()} contacts remaining{sentSoFar > 0 ? ` · ${sentSoFar.toLocaleString()} already queued` : ''}.</p>

                <div className="flex flex-wrap gap-1.5 mb-2">
                  {presets.map(n => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setBatchSize(n)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition
                        ${(batchSize || (c.batch_limit || 25)) === n
                          ? 'bg-brand-700 text-white border-brand-700'
                          : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`}
                    >{n}</button>
                  ))}
                  <input
                    type="number"
                    min="1"
                    placeholder="Custom"
                    value={batchSize || ''}
                    onChange={e => setBatchSize(Number(e.target.value) || 0)}
                    className="w-20 h-7 px-2 rounded-full border border-slate-200 text-xs text-center focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-500"
                  />
                </div>

                <button className="btn w-full mb-2" disabled={busy || remaining === 0 || c.status === 'paused' || c.status === 'cancelled'} onClick={() => setConfirmOpen('first_n')}>
                  {busy ? '…' : remaining === 0 ? 'Nothing left to send' : `${verb} ${effectiveBatch.toLocaleString()}`}
                </button>
                <button className="btn-ghost w-full" disabled={busy || remaining === 0 || c.status === 'paused' || c.status === 'cancelled'} onClick={() => setConfirmOpen('all')}>
                  {busy ? '…' : `Send all ${remaining.toLocaleString()} remaining`}
                </button>
                <p className="text-[11px] text-slate-500 mt-3">Sends are simulated unless the server is in <strong>LIVE</strong> mode (red sidebar banner).</p>
              </div>
            );
          })()}

          {Number(counts.failed_retryable || 0) > 0 && c.status !== 'done' && c.status !== 'cancelled' && (
            <div className="card p-5 border-amber-200 bg-amber-50/30">
              <h4 className="text-sm font-semibold text-amber-800 mb-1">Retry failed</h4>
              <p className="text-xs text-amber-700 mb-3">{counts.failed_retryable} contacts failed with retryable Meta error codes (window-expired, rate-limit, daily cap…).</p>
              <button className="btn w-full" disabled={busy} onClick={retryFailed}>Re-queue these {counts.failed_retryable}</button>
              <p className="text-[11px] text-amber-700/80 mt-2">Each retry counts as one attempt of 7 max per contact.</p>
            </div>
          )}

          {/* Status actions */}
          {c.status !== 'done' && c.status !== 'cancelled' && (
            <div className="card p-5">
              <h4 className="text-sm font-semibold text-slate-900 mb-3">Lifecycle</h4>
              <div className="space-y-2">
                {c.status === 'running' && (
                  <button className="btn-ghost w-full" disabled={busy} onClick={() => changeStatus('paused', 'Pause sending? Worker will skip this campaign\'s queued rows until you resume.')}>
                    Pause
                  </button>
                )}
                {c.status === 'paused' && (
                  <button className="btn w-full" disabled={busy} onClick={() => changeStatus('running')}>
                    Resume
                  </button>
                )}
                {(c.status === 'queued' || c.status === 'running' || c.status === 'paused') && (
                  <button className="btn-ghost w-full" disabled={busy} onClick={() => changeStatus('done', 'Mark complete? Retry buttons disable.')}>
                    Mark complete
                  </button>
                )}
                <button className="btn-ghost w-full text-rose-700 border-rose-100 hover:bg-rose-50" disabled={busy} onClick={() => changeStatus('cancelled', 'Cancel campaign? Queued rows stay in DB but worker will not process them. Cannot resume after this.')}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {(c.status === 'done' || c.status === 'cancelled') && (
            <div className="card p-5 bg-slate-50/40">
              <h4 className="text-sm font-semibold text-slate-700 mb-1 capitalize">{c.status}</h4>
              <p className="text-xs text-slate-500">{c.completed_at ? `Closed at ${c.completed_at}` : 'No further sending or retries.'}</p>
            </div>
          )}
        </aside>
      </div>

      <Modal open={confirmOpen !== null} onClose={() => setConfirmOpen(null)}
        title={confirmOpen === 'all' ? 'Send to everyone remaining?' : `Send next ${(batchSize || c.batch_limit || 25).toLocaleString()}?`}
        footer={<>
          <button className="btn-ghost" onClick={() => setConfirmOpen(null)}>Cancel</button>
          <button className="btn" disabled={busy} onClick={() => queue(confirmOpen)}>{busy ? '…' : 'Confirm'}</button>
        </>}>
        <p className="text-sm text-slate-700">
          {confirmOpen === 'all'
            ? `This will queue every remaining contact (${(Math.max(0, (preview?.total ?? 0) - Number(counts.total || 0))).toLocaleString()}). The worker dispatches them at ~10 / second.`
            : `Queues up to ${(batchSize || c.batch_limit || 25).toLocaleString()} contacts in this bucket. Watch delivery, then come back tomorrow (or later) to send the next bucket.`
          }
        </p>
        <p className="text-xs text-slate-500 mt-3">Actual sending requires server SEND_MODE=live (sidebar banner). In dry-run, rows are still queued and the worker logs "would send" without calling Meta / SendGrid.</p>
      </Modal>
    </>
  );
}

/* Comma-separated phones + emails fired through the campaign-as-configured.
 * Useful for verifying message + link before queuing real recipients. */
function TestSendPanel({ campaignId }) {
  const [tagSlug, setTagSlug] = useState('');
  const [phones, setPhones]   = useState('');
  const [emails, setEmails]   = useState('');
  const [live, setLive]       = useState(false);
  const [busy, setBusy]       = useState(false);
  const [results, setResults] = useState([]);
  const [statusByAttempt, setStatusByAttempt] = useState({});
  const [tags, setTags]       = useState([]);

  useEffect(() => {
    api.get('/api/tags').then(r => setTags(r.rows || [])).catch(() => {});
  }, []);

  // Poll the delivery status of any test sends with attempt_id for ~90s.
  useEffect(() => {
    const ids = results.map(r => r.attempt_id).filter(Boolean);
    if (!ids.length) return;
    const startedAt = Date.now();
    let cancelled = false;
    async function poll() {
      try {
        const r = await api.get(`/api/campaigns/test-status?ids=${ids.join(',')}`);
        if (cancelled) return;
        const map = {};
        for (const a of (r.rows || [])) map[a.id] = a;
        setStatusByAttempt(prev => ({ ...prev, ...map }));
      } catch {}
    }
    poll();
    const t = setInterval(() => {
      if (Date.now() - startedAt > 90_000) { clearInterval(t); return; }
      poll();
    }, 4000);
    return () => { cancelled = true; clearInterval(t); };
  }, [results]);

  async function send() {
    if (!tagSlug && !phones.trim() && !emails.trim()) return;
    if (live && !window.confirm('Live test: real WhatsApp + email will be sent to the recipients. Continue?')) return;
    setBusy(true);
    setResults([]);
    setStatusByAttempt({});
    try {
      const r = await api.post(`/api/campaigns/${campaignId}/send-test-batch`, {
        tag_slug: tagSlug || undefined,
        phones: phones.trim(),
        emails: emails.trim(),
        live,
      });
      setResults(r.results || []);
    } catch (e) {
      setResults([{ kind: 'error', input: '', error: e.body?.error || e.message }]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Test send</h3>
          <p className="text-xs text-slate-500 mt-0.5">Fires the campaign-as-configured to ad-hoc recipients. Stays out of campaign analytics.</p>
        </div>
      </div>

      {/* Tag-based test (preferred) — sends to existing real contacts in a tag */}
      <div className="mb-3">
        <div className="label-l">Pick a tag to send to (recommended — uses real contacts)</div>
        <select className="input w-full" value={tagSlug} onChange={e => setTagSlug(e.target.value)}>
          <option value="">— don't use tag, paste numbers/emails below —</option>
          {tags.map(t => <option key={t.id} value={t.slug}>{t.name} ({t.contact_count.toLocaleString()})</option>)}
        </select>
        <div className="text-[11px] text-slate-500 mt-1">Picks up to 50 contacts in the tag. Existing tokens are reused (link stays valid across re-tests).</div>
      </div>

      <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 ${tagSlug ? 'opacity-50 pointer-events-none' : ''}`}>
        <div>
          <div className="label-l">Phones (comma-separated)</div>
          <textarea
            className="input w-full h-20 font-mono text-xs"
            placeholder="9986104646, 9900482011"
            value={phones}
            onChange={e => setPhones(e.target.value)}
            disabled={!!tagSlug}
          />
        </div>
        <div>
          <div className="label-l">Emails (comma-separated)</div>
          <textarea
            className="input w-full h-20 font-mono text-xs"
            placeholder="bk@grsfantasypark.com, ashwin@grsfantasypark.com"
            value={emails}
            onChange={e => setEmails(e.target.value)}
            disabled={!!tagSlug}
          />
        </div>
      </div>

      <div className="mt-3 flex items-center gap-3">
        <label className={`inline-flex items-center gap-2 text-sm ${live ? 'text-rose-700 font-semibold' : 'text-slate-600'}`}>
          <input type="checkbox" checked={live} onChange={e => setLive(e.target.checked)} />
          Live (real send, bypass dry-run)
        </label>
        <div className="flex-1" />
        <button className="btn" onClick={send} disabled={busy}>{busy ? 'Sending…' : 'Send test'}</button>
      </div>

      {results.length > 0 && (
        <ul className="mt-4 divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden text-xs">
          {results.map((r, i) => {
            const status = r.attempt_id ? statusByAttempt[r.attempt_id] : null;
            return (
              <li key={i} className="px-3 py-2 flex items-center gap-3">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 w-12 shrink-0">{r.kind}</span>
                <span className="text-slate-700 flex-1 truncate">{r.input}</span>
                <div className="flex items-center gap-1.5 shrink-0">
                  {r.dry_run && <Chip>dry-run ok</Chip>}
                  {!r.dry_run && r.ok && <Chip tone="emerald">sent</Chip>}
                  {!r.dry_run && status?.delivered_at && <Chip tone="emerald">delivered</Chip>}
                  {!r.dry_run && status?.read_at && <Chip tone="sky">{r.kind === 'wa' ? 'read' : 'opened'}</Chip>}
                  {!r.dry_run && status?.clicked_at && <Chip tone="brand">clicked</Chip>}
                  {!r.dry_run && status?.failed_at && <Chip tone="rose" title={status.failure_kind || ''}>{status.failure_kind || 'failed'}</Chip>}
                  {!r.ok && r.error && <Chip tone="rose" title={r.error}>{r.error.slice(0, 40)}</Chip>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {results.some(r => r.attempt_id) && (
        <p className="mt-2 text-[11px] text-slate-500">Delivery status updates live for ~90s as Meta / SendGrid send back webhook events. WA "read" only fires if the recipient has read receipts on.</p>
      )}
    </div>
  );
}

/* ============================================================================
 * DASHBOARD — funnel, channel split, per-run, failure breakdown, recipients.
 * ============================================================================ */
function DashboardSection({ campaignId }) {
  const [tab, setTab] = useState('overview');
  const [m, setM] = useState(null);

  async function load() {
    try { setM(await api.get(`/api/campaigns/${campaignId}/metrics`)); } catch {}
  }
  useEffect(() => { load(); /* eslint-disable-line */ }, [campaignId]);
  useEffect(() => { const t = setInterval(load, 10000); return () => clearInterval(t); /* eslint-disable-line */ }, [campaignId]);

  function downloadCsv() {
    const tok = getToken();
    fetch(`/api/campaigns/${campaignId}/sends/export.csv`, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.blob())
      .then(b => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(b);
        a.download = `campaign-${campaignId}-sends.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  }

  if (!m) return <div className="card p-6 text-sm text-slate-400">Loading metrics…</div>;

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex border-b border-slate-200 -mb-px">
          <DashTab active={tab === 'overview'}    onClick={() => setTab('overview')}>Overview</DashTab>
          <DashTab active={tab === 'per_run'}     onClick={() => setTab('per_run')}>Per-run</DashTab>
          <DashTab active={tab === 'failures'}    onClick={() => setTab('failures')}>Failures</DashTab>
          <DashTab active={tab === 'recipients'}  onClick={() => setTab('recipients')}>Recipients</DashTab>
        </div>
        <button className="btn-ghost btn-sm" onClick={downloadCsv}>Download CSV</button>
      </div>

      {tab === 'overview'   && <Overview m={m} />}
      {tab === 'per_run'    && <PerRun m={m} />}
      {tab === 'failures'   && <Failures m={m} campaignId={campaignId} onRequeued={load} />}
      {tab === 'recipients' && <Recipients campaignId={campaignId} />}
    </>
  );
}

function DashTab({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`px-3.5 py-2 text-sm font-medium transition border-b-2
      ${active ? 'border-brand-700 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>{children}</button>
  );
}

/* ---------- Overview: funnel bars + channel split cards ---------- */
function Overview({ m }) {
  const wa    = m.funnel.wa;
  const email = m.funnel.email;
  const eng   = m.engagement;

  const totalRecipients = (wa.recipients || 0) + (email.recipients || 0);
  const totalSent       = (wa.sent || 0) + (email.sent || 0);
  const totalDelivered  = (wa.delivered || 0) + (email.delivered || 0);

  return (
    <div className="space-y-4">
      <div className="card p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Funnel</h3>
        <FunnelBar label="Recipients"       value={totalRecipients} max={totalRecipients} />
        <FunnelBar label="Sent"             value={totalSent}       max={totalRecipients} />
        <FunnelBar label="Delivered"        value={totalDelivered}  max={totalRecipients} />
        <FunnelBar label="Read / Opened"    value={(wa.read_or_opened||0) + (email.read_or_opened||0)} max={totalRecipients} />
        <FunnelBar label="Clicked"          value={eng.clicked}     max={totalRecipients} />
        <FunnelBar label="Submitted"        value={eng.submitted}   max={totalRecipients} accent="emerald" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChannelCard title="WhatsApp"  data={m.funnel.wa}    statusCounts={m.by_channel.wa}    />
        <ChannelCard title="Email"     data={m.funnel.email} statusCounts={m.by_channel.email} engLabel="Opened" />
      </div>
    </div>
  );
}

function FunnelBar({ label, value, max, accent = 'brand' }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  const bg = accent === 'emerald' ? 'bg-emerald-600' : 'bg-brand-700';
  return (
    <div className="mb-2.5 last:mb-0">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-slate-700 font-medium">{label}</span>
        <span className="text-slate-500 tabular-nums">{value.toLocaleString()} <span className="text-slate-400 ml-1">{pct}%</span></span>
      </div>
      <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${bg} rounded-full transition-all`} style={{ width: `${Math.max(pct, value > 0 ? 2 : 0)}%` }} />
      </div>
    </div>
  );
}

function ChannelCard({ title, data, statusCounts, engLabel = 'Read' }) {
  const sentRate = data.recipients > 0 ? Math.round((data.sent / data.recipients) * 100) : 0;
  const delRate  = data.sent > 0       ? Math.round((data.delivered / data.sent) * 100) : 0;
  const engRate  = data.delivered > 0  ? Math.round((data.read_or_opened / data.delivered) * 100) : 0;
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
        <span className="text-[10px] uppercase tracking-wider text-slate-500">{(data.recipients || 0).toLocaleString()} recipients</span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <Mini label="Sent"     value={data.sent} pct={sentRate} />
        <Mini label="Delivered" value={data.delivered} pct={delRate} accent="emerald" />
        <Mini label={engLabel}  value={data.read_or_opened} pct={engRate} accent="sky" />
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600 flex flex-wrap gap-3">
        {statusCounts.queued   > 0 && <span>Queued: <strong className="text-amber-700">{statusCounts.queued}</strong></span>}
        {statusCounts.sending  > 0 && <span>Sending: <strong>{statusCounts.sending}</strong></span>}
        {statusCounts.failed   > 0 && <span>Failed: <strong className="text-rose-700">{statusCounts.failed}</strong>{statusCounts.failed_retryable ? <span className="text-amber-600"> ({statusCounts.failed_retryable} retryable)</span> : null}</span>}
      </div>
    </div>
  );
}

function Mini({ label, value, pct, accent = 'slate' }) {
  const cls = accent === 'emerald' ? 'text-emerald-700'
    : accent === 'sky' ? 'text-sky-700'
    : 'text-slate-900';
  return (
    <div>
      <div className={`text-2xl font-semibold ${cls}`}>{value.toLocaleString()}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{label}</div>
      <div className="text-[10px] text-slate-400 mt-0.5">{pct}%</div>
    </div>
  );
}

/* ---------- Succeeded-on-attempt-N histogram ---------- */
function SuccessAttemptHistogram({ rows }) {
  if (!rows || !rows.length) return null;
  const total = rows.reduce((s, r) => s + r.contact_count, 0);
  const max   = Math.max(1, ...rows.map(r => r.contact_count));
  return (
    <div className="card p-5">
      <h3 className="text-sm font-semibold text-slate-900 mb-1">Which attempt finally landed</h3>
      <p className="text-xs text-slate-500 mb-4">For every contact in this campaign, the attempt # on which their first <strong>delivered</strong> dispatch occurred. Tells you how Meta's policy windows reopen with retries.</p>
      <div className="space-y-2.5">
        {rows.map(r => {
          const pct  = Math.round((r.contact_count / total) * 100);
          const bar  = Math.max(2, Math.round((r.contact_count / max) * 100));
          const isMissing = r.first_success === null;
          const label = isMissing ? 'Never landed (still failed / pending)' : `Attempt #${r.first_success}`;
          const tone = isMissing ? 'bg-rose-500' : r.first_success === 1 ? 'bg-emerald-500' : 'bg-amber-500';
          return (
            <div key={String(r.first_success)}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className={`font-medium ${isMissing ? 'text-rose-700' : 'text-slate-700'}`}>{label}</span>
                <span className="text-slate-500 tabular-nums">{r.contact_count.toLocaleString()} <span className="text-slate-400 ml-1">{pct}%</span></span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full ${tone}`} style={{ width: `${bar}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Per-run table ---------- */
function PerRun({ m }) {
  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of m.by_attempt) {
      if (!map.has(r.attempt_no)) map.set(r.attempt_no, { attempt_no: r.attempt_no, wa: null, email: null });
      map.get(r.attempt_no)[r.channel] = r;
    }
    return [...map.values()].sort((a, b) => a.attempt_no - b.attempt_no);
  }, [m.by_attempt]);

  // Find the most successful run by combined delivery rate.
  const best = grouped.reduce((acc, r) => {
    const tot = (r.wa?.dispatched || 0) + (r.email?.dispatched || 0);
    const del = (r.wa?.delivered || 0)  + (r.email?.delivered || 0);
    const rate = tot > 0 ? del / tot : 0;
    return (!acc || rate > acc.rate) ? { attempt_no: r.attempt_no, rate, dispatched: tot, delivered: del } : acc;
  }, null);

  return (
    <div className="space-y-4">
      {best && best.dispatched > 0 && (
        <div className="card p-5 bg-emerald-50/40 border-emerald-200">
          <h4 className="text-sm font-semibold text-emerald-800">Most successful run: attempt #{best.attempt_no}</h4>
          <p className="text-xs text-emerald-700 mt-0.5">
            <strong>{best.delivered.toLocaleString()}</strong> delivered out of <strong>{best.dispatched.toLocaleString()}</strong> dispatched ({Math.round(best.rate * 100)}%).
          </p>
        </div>
      )}
      <SuccessAttemptHistogram rows={m.by_success_attempt} />
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2.5 text-left">Attempt</th>
              <th className="px-3 py-2.5 text-left">Channel</th>
              <th className="px-3 py-2.5 text-right">Dispatched</th>
              <th className="px-3 py-2.5 text-right">Delivered</th>
              <th className="px-3 py-2.5 text-right">Read/Opened</th>
              <th className="px-3 py-2.5 text-right">Delivery rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {grouped.map(r => ['wa','email'].map(ch => r[ch] && (
              <tr key={`${r.attempt_no}-${ch}`} className="hover:bg-slate-50/60">
                <td className="px-3 py-2 font-medium text-slate-900">#{r.attempt_no}</td>
                <td className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500">{ch}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r[ch].dispatched}</td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{r[ch].delivered}</td>
                <td className="px-3 py-2 text-right tabular-nums text-sky-700">{r[ch].read_or_opened}</td>
                <td className="px-3 py-2 text-right tabular-nums">{Math.round(r[ch].delivery_rate * 100)}%</td>
              </tr>
            ))).flat().filter(Boolean)}
            {!grouped.length && <tr><td colSpan={6} className="px-3 py-12 text-center text-slate-400">No dispatch attempts yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Failure breakdown with retry buttons ---------- */
function Failures({ m, campaignId, onRequeued }) {
  const [busy, setBusy] = useState(null);

  async function retry(errorCode) {
    setBusy(errorCode);
    try {
      const r = await api.post(`/api/campaigns/${campaignId}/retry-failed`, { error_code: errorCode });
      if (onRequeued) onRequeued();
      alert(`Re-queued ${r.requeued} sends.`);
    } catch (e) { alert(e.body?.error || e.message); }
    finally { setBusy(null); }
  }

  if (!m.failures.length) {
    return <div className="card p-12 text-center text-slate-400 text-sm">No failures yet.</div>;
  }

  return (
    <div className="card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="px-3 py-2.5 text-left">Channel</th>
            <th className="px-3 py-2.5 text-left">Error code</th>
            <th className="px-3 py-2.5 text-left">Reason</th>
            <th className="px-3 py-2.5 text-right">Failed</th>
            <th className="px-3 py-2.5 text-right">Retryable</th>
            <th className="px-3 py-2.5 text-right pr-4"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {m.failures.map((f, i) => (
            <tr key={i} className="hover:bg-slate-50/60">
              <td className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500">{f.channel}</td>
              <td className="px-3 py-2 font-mono text-xs">{f.error_code}</td>
              <td className="px-3 py-2 text-slate-700 truncate max-w-[300px]">{f.label}</td>
              <td className="px-3 py-2 text-right tabular-nums text-rose-700 font-semibold">{f.count}</td>
              <td className="px-3 py-2 text-right tabular-nums text-amber-700">{f.retryable_count}</td>
              <td className="px-3 py-2 pr-4 text-right">
                {f.retryable && f.retryable_count > 0 ? (
                  <button className="btn-ghost btn-sm" onClick={() => retry(f.error_code)} disabled={busy === f.error_code}>
                    {busy === f.error_code ? '…' : `Retry these ${f.retryable_count}`}
                  </button>
                ) : <span className="text-[11px] text-slate-400">permanent</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Recipients list with click-to-timeline ---------- */
function Recipients({ campaignId }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState(null);
  const limit = 30;

  async function load(off = 0) {
    try {
      const r = await api.get(`/api/campaigns/${campaignId}/contacts?limit=${limit}&offset=${off}`);
      setRows(r.rows); setTotal(r.total); setOffset(off);
    } catch {}
  }
  useEffect(() => { load(0); /* eslint-disable-line */ }, [campaignId]);

  return (
    <>
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2.5 text-left">Contact</th>
                <th className="px-3 py-2.5 text-left">Channel</th>
                <th className="px-3 py-2.5 text-left">Status</th>
                <th className="px-3 py-2.5 text-right">Attempts</th>
                <th className="px-3 py-2.5 text-left">Error</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(r => (
                <tr key={r.send_id} className="hover:bg-slate-50/60">
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-900">{r.name}</div>
                    <div className="text-xs text-slate-500">{r.channel === 'wa' ? '+'+r.phone : r.email}</div>
                  </td>
                  <td className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500">{r.channel}</td>
                  <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.attempt_count}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 truncate max-w-[220px]" title={r.error || ''}>{r.error_code || (r.error ? r.error.slice(0, 40) : '')}</td>
                  <td className="px-3 py-2 text-right pr-4">
                    <button className="btn-ghost btn-sm" onClick={() => setOpen(r)}>View attempts</button>
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} className="px-3 py-12 text-center text-slate-400">No recipients yet.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <span>{offset + 1}–{Math.min(offset + limit, total)} of {total.toLocaleString()}</span>
          <div className="flex gap-2">
            <button className="btn-ghost btn-sm" disabled={offset === 0} onClick={() => load(Math.max(0, offset - limit))}>← Prev</button>
            <button className="btn-ghost btn-sm" disabled={offset + limit >= total} onClick={() => load(offset + limit)}>Next →</button>
          </div>
        </div>
      </div>

      <Modal open={!!open} onClose={() => setOpen(null)} title={open ? `${open.name} · ${open.channel.toUpperCase()}` : ''} maxWidth="max-w-2xl">
        {open && <ContactTimeline campaignId={campaignId} sendId={open.send_id} />}
      </Modal>
    </>
  );
}

function ContactTimeline({ campaignId, sendId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/api/campaigns/${campaignId}/contacts/${sendId}/attempts`).then(r => setRows(r.rows)).catch(() => setRows([]));
  }, [campaignId, sendId]);
  if (!rows) return <div className="text-sm text-slate-400">Loading…</div>;
  if (!rows.length) return <div className="text-sm text-slate-400">No attempts yet.</div>;
  return (
    <ul className="space-y-3">
      {rows.map(a => (
        <li key={a.id} className="rounded-lg border border-slate-200 p-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-semibold text-slate-900">Attempt #{a.attempt_no}</span>
            <StatusBadge status={a.status} />
            {a.error_code && <code className="text-xs bg-rose-50 text-rose-700 px-1.5 py-0.5 rounded">{a.error_code}</code>}
          </div>
          <ul className="text-xs space-y-1">
            <TimelineRow label="Dispatched"   ts={a.attempted_at} />
            {a.delivered_at && <TimelineRow label="Delivered" ts={a.delivered_at} ok />}
            {a.read_at      && <TimelineRow label="Read / Opened" ts={a.read_at} ok />}
            {a.clicked_at   && <TimelineRow label="Clicked link" ts={a.clicked_at} ok />}
            {a.failed_at    && <TimelineRow label={`Failed${a.failure_kind ? ' · '+a.failure_kind : ''}`} ts={a.failed_at} bad />}
          </ul>
          {a.error && <div className="mt-2 text-[11px] text-rose-700">{a.error}</div>}
        </li>
      ))}
    </ul>
  );
}

function TimelineRow({ label, ts, ok, bad }) {
  return (
    <li className="flex items-center gap-2">
      <span className={`w-1.5 h-1.5 rounded-full ${ok ? 'bg-emerald-500' : bad ? 'bg-rose-500' : 'bg-slate-400'}`} />
      <span className={ok ? 'text-emerald-700' : bad ? 'text-rose-700' : 'text-slate-700'}>{label}</span>
      <span className="text-slate-400 tabular-nums ml-auto">{fmtIST(ts)}</span>
    </li>
  );
}

function Chip({ children, tone, title }) {
  const cls = tone === 'emerald' ? 'bg-emerald-50 text-emerald-700'
    : tone === 'sky'   ? 'bg-sky-50 text-sky-700'
    : tone === 'brand' ? 'bg-brand-50 text-brand-700'
    : tone === 'rose'  ? 'bg-rose-50 text-rose-700 max-w-xs truncate'
    : 'bg-slate-100 text-slate-600';
  return <span title={title} className={`px-2 py-0.5 rounded text-[11px] font-medium ${cls}`}>{children}</span>;
}

function Stat({ label, value, tone = 'slate' }) {
  const tones = {
    slate:   'text-slate-900',
    brand:   'text-brand-700',
    emerald: 'text-emerald-700',
    amber:   'text-amber-700',
    rose:    'text-rose-700',
  };
  return (
    <div className="card p-4">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 text-xl font-semibold ${tones[tone]}`}>{value.toLocaleString()}</div>
    </div>
  );
}
